import React, { useState } from 'react';
import Head from 'next/head';
import { useRouter } from 'next/router';
import clsx from 'clsx';
import { Icon24SearchOutline } from '@vkontakte/icons';
import { faChevronLeft } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import Text from '@/components/Text';
import Button, { ButtonType } from '@/components/Button';
import Input, { InputHeight, InputStyle } from '@/components/Input';
import Loader from '@/components/Loader';
import AssetImage from '@/components/AssetImage';

import useSearch from '@/hooks/useSearch';
import useTop25Stocks from '@/hooks/useTop25Stocks';
import useRecentlyViewed from '@/hooks/useRecentlyViewed';
import useDebounce from '@/hooks/useDebounce';
import useCategory from '@/hooks/useCategory';
import { useAuthUser } from '@/hooks/useAuthUser';
import { firebaseAuth } from '@/lib/Firebase';
import { useLoginModal } from '@/common/LoginModal';

import Heading from '@/common/Heading';
import Section from '@/common/Section';
import ListItem from '@/common/ListItem';
import Categories from '@/pages/discover/Categories';
import { INDUSTRIES } from './industries';
import IndustryListItem from './IndustryListItem';
import StyleUtils from '@/utils/StyleUtils';
import MathUtils from '@/utils/MathUtils';
import StringUtils from '@/utils/StringUtils';

interface BrowseConceptProps {
  onSelect: (stockName: string, ticker: string, isIndustry: boolean) => void;
  onBack?: () => void;
}

type Tab = 'trending' | 'categories' | 'industries';

export default function BrowseConcept({ onSelect, onBack }: BrowseConceptProps) {
  const router = useRouter();
  const { data: authUser } = useAuthUser(['authUser'], firebaseAuth);
  const isAuthenticated = !!authUser;
  const { requireAuth } = useLoginModal();

  // State
  const [query, setQuery] = useState('');
  const debouncedQuery = useDebounce(query, 300);
  const [selectedStock, setSelectedStock] = useState<any | null>(null);
  const [selectedIndustry, setSelectedIndustry] = useState<any | null>(null);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [activeTab, setActiveTab] = useState<Tab>(isAuthenticated ? 'trending' : 'industries');

  React.useEffect(() => {
    setIsVisible(true);
  }, []);

  // Hooks
  const { data: searchResults, isLoading: isSearchLoading } = useSearch(debouncedQuery as string);
  const { data: top25Stocks, isLoading: isTop25Loading } = useTop25Stocks();
  const { recentlyViewed } = useRecentlyViewed();
  const { data: selectedCategoryData, isLoading: isCategoryLoading } = useCategory(selectedCategoryId || '');

  // Handle selection
  const handleStockSelect = (stock: any) => {
    setSelectedStock(stock);
    setSelectedIndustry(null);
  };

  const handleIndustrySelect = (industry: any) => {
    setSelectedIndustry(industry);
    setSelectedStock(null);
  };

  const onChoosePreferences = () => {
    if (selectedStock) {
      onSelect(selectedStock.name || selectedStock.ticker, selectedStock.ticker, false);
    } else if (selectedIndustry) {
      onSelect(selectedIndustry.name, selectedIndustry.slug, true);
    }
  };

  const isButtonEnabled = !!selectedStock || !!selectedIndustry;

  return (
    <>
      <div className={clsx(
        "flex flex-col gap-6 transition-all duration-700 ease-out",
        isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
      )}>
      <Head>
        <title>Pick a Stock | Bloom</title>
      </Head>

      <div className="flex justify-between items-center gap-4 py-2 mb-0 px-0">
        {selectedCategoryId ? (
          <Heading as="h1" asBack onBack={() => setSelectedCategoryId(null)}>
            {selectedCategoryData?.name || 'Category'}
          </Heading>
        ) : (
          <>
            <Button 
              type="IconButton" 
              className='bg-btn-primary flex-none !w-8 !h-8 md:!w-10 md:!h-10 !p-0 flex items-center justify-center' 
              onClick={onBack}
            >
              <FontAwesomeIcon icon={faChevronLeft} className="w-3 h-3 text-white flex-shrink-0" />
            </Button>
            <h1 className="flex items-center gap-2 font-sans font-normal text-base md:text-lg capitalize flex-grow">
              Pick a Stock
            </h1>
          </>
        )}
      </div>

      {!selectedCategoryId && (
        <div className="px-0 animate-in fade-in slide-in-from-top-2 duration-500 space-y-6">
          {isAuthenticated && (
            <Input
              type="text"
              inputStyle={InputStyle.Primary}
              inputHeight={InputHeight.Medium}
              className="bg-transparent focus:ring-1 transition-all duration-300 focus:bg-white/5"
              iconLeft={<Icon24SearchOutline className="text-icon-primary" />}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search for stocks..."
              autoFocus
            />
          )}

          {/* Tabs */}
          {query.length === 0 && (() => {
            const tabs = isAuthenticated
              ? (['trending', 'categories', 'industries'] as const)
              : (['industries', 'categories'] as const);
            const tabWidth = `calc(${100 / tabs.length}% - 4px)`;
            const tabIndex = tabs.indexOf(activeTab as any);
            const tabLeft = tabIndex >= 0 ? `calc(${(tabIndex / tabs.length) * 100}% + 2px)` : '4px';

            return (
              <div className="relative flex p-1 bg-white/5 rounded-full border border-white/5">
                {/* Sliding Background */}
                <div
                  className="absolute inset-y-1 rounded-full bg-white/10 shadow-xl transition-all duration-300 ease-out border border-white/10"
                  style={{ width: tabWidth, left: tabLeft }}
                />
                {tabs.map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={clsx(
                      "relative z-10 flex-1 py-2 text-sm font-medium transition-colors duration-300 rounded-full",
                      activeTab === tab ? "text-white" : "text-secondary hover:text-primary"
                    )}
                  >
                    {tab === 'trending' ? 'Trending' : tab === 'categories' ? 'Categories' : 'Industries'}
                  </button>
                ))}
              </div>
            );
          })()}
        </div>
      )}

      {/* Content Area */}
      <div className="flex-1 mt-2">
        {selectedCategoryId ? (
          /* Category Sub-page */
          <div className="space-y-6 pb-24 px-0 animate-in fade-in slide-in-from-right-4 duration-500">
            <Section title={selectedCategoryData?.name || 'Stocks'} titleInside className="pt-5 pb-5 px-0">
              <div className="divide-y divide-line-primary/30 overflow-hidden">
                {isCategoryLoading ? (
                  [1, 2, 3, 4, 5].map((i) => <div key={i} className="h-16 animate-pulse bg-white/5 rounded-xl mb-2 mx-4" />)
                ) : selectedCategoryData?.assets?.length ? (
                  selectedCategoryData.assets.map((asset: any, index: number) => (
                    <StockItemRow 
                      key={asset.ticker}
                      symbol={asset.ticker}
                      name={asset.name || asset.ticker}
                      image={asset.image}
                      isSelected={selectedStock?.ticker === asset.ticker}
                      onSelect={() => handleStockSelect(asset)}
                      isFirst={index === 0}
                      isLast={index === (selectedCategoryData?.assets?.length || 0) - 1}
                      changePercent={asset.latestQuote?.changePercent}
                      latestPrice={asset.latestQuote?.latestPrice}
                    />
                  ))
                ) : (
                  <div className="py-10 text-center text-secondary">No stocks found in this category</div>
                )}
              </div>
            </Section>
          </div>
        ) : query.length > 0 ? (
          /* Search Results */
          <div className="rounded-2xl bg-darkglass mt-4 py-6 animate-in fade-in zoom-in-95 duration-300">
            {isSearchLoading ? (
              <div className="flex justify-center py-10">
                <Loader />
              </div>
            ) : searchResults?.stocks?.length ? (
              <>
                <h4 className="mb-2 font-medium pl-6 text-primary">
                  {searchResults.stocks.length} results found
                </h4>
                <ul className="flex flex-col overflow-hidden rounded-xl">
                {searchResults.stocks.map((stock: any, index: number) => (
                  <StockItemRow 
                    key={stock.ticker}
                    symbol={stock.ticker}
                    name={stock.name}
                    image={stock.image}
                    isSelected={selectedStock?.ticker === stock.ticker}
                    onSelect={() => handleStockSelect(stock)}
                    isFirst={index === 0}
                    isLast={index === searchResults.stocks.length - 1}
                    changePercent={stock.latestQuote?.changePercent}
                    latestPrice={stock.latestQuote?.latestPrice}
                  />
                ))}
                </ul>
              </>
            ) : (
              <div className="text-center py-10">
                <h4 className="mb-2 font-medium text-secondary">No results found</h4>
                <Text color="secondary">No results for &quot;{query}&quot;</Text>
              </div>
            )}
          </div>
        ) : (
          /* Default Content */
          <div className="space-y-6 pb-24 px-0">
            {activeTab === 'trending' && (
              <div className="animate-in fade-in slide-in-from-left-4 duration-500 space-y-6">
                {/* Recently Viewed */}
                {recentlyViewed.length > 0 && (
                  <Section title="Recently viewed stocks" titleInside>
                    <div className="flex space-x-6 overflow-x-auto pb-4 scrollbar-hide px-6">
                      {recentlyViewed.map((symbol) => (
                        <button 
                          key={symbol}
                          onClick={() => handleStockSelect({ ticker: symbol, name: symbol })}
                          className="flex flex-col items-center space-y-3 min-w-[72px] group transition-transform hover:scale-105"
                        >
                          <div className="relative">
                            <AssetImage alt={symbol} src="" width={64} height={64} className="w-16 h-16 rounded-2xl group-hover:shadow-lg transition-all" />
                            {selectedStock?.ticker === symbol && (
                              <div className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-success border-2 border-black flex items-center justify-center">
                                <div className="h-2 w-2 rounded-full bg-white" />
                              </div>
                            )}
                          </div>
                          <Text size="xs" color={selectedStock?.ticker === symbol ? 'primary' : 'secondary'}>{symbol}</Text>
                        </button>
                      ))}
                    </div>
                  </Section>
                )}

                {/* Trending Stocks */}
                <Section title="Trending Stocks" titleInside>
                  <div className="divide-y divide-line-primary/30 overflow-hidden">
                    {isTop25Loading ? (
                      [1, 2, 3].map((i) => <div key={i} className="h-16 animate-pulse bg-white/5 mb-2" />)
                    ) : top25Stocks?.length > 0 ? (
                      top25Stocks.map((stock: any, index: number) => (
                        <StockItemRow 
                          key={stock.ticker}
                          symbol={stock.ticker}
                          name={stock.name || stock.ticker}
                          image={stock.image}
                          isSelected={selectedStock?.ticker === stock.ticker}
                          onSelect={() => handleStockSelect(stock)}
                          isFirst={index === 0}
                          isLast={index === top25Stocks.length - 1}
                          changePercent={stock.latestQuote?.changePercent}
                          latestPrice={stock.latestQuote?.latestPrice}
                        />
                      ))
                    ) : null}
                  </div>
                </Section>
              </div>
            )}

            {activeTab === 'categories' && (
              <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
                {isAuthenticated ? (
                  <Categories className="mt-0" onCategoryClick={(cat) => setSelectedCategoryId(cat.id.toString())} />
                ) : (
                  <div className="rounded-2xl bg-darkglass py-12 px-6 text-center space-y-4">
                    <h4 className="text-primary text-lg font-medium">Log in to unlock all stocks</h4>
                    <Text color="secondary">Sign in to browse stocks by category and search for specific tickers.</Text>
                    <Button
                      type={ButtonType.Primary}
                      size="md"
                      className="mt-2"
                      onClick={() => requireAuth()}
                    >
                      Log in
                    </Button>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'industries' && (
              <div className="animate-in fade-in slide-in-from-right-4 duration-500">
                <Section title="Industries" titleInside>
                  <div className="divide-y divide-line-primary/30 overflow-hidden">
                    {INDUSTRIES.map((industry, index) => (
                      <IndustryListItem 
                        key={industry.slug}
                        name={industry.name}
                        slug={industry.slug}
                        description={industry.description}
                        isSelected={selectedIndustry?.slug === industry.slug}
                        index={index}
                        isFirst={index === 0}
                        isLast={index === INDUSTRIES.length - 1}
                        onPress={() => handleIndustrySelect(industry)}
                      />
                    ))}
                  </div>
                </Section>
              </div>
            )}
          </div>
        )}
        </div>
      </div>

      {/* Bottom Button Area - Outside container with transform to allow fixed positioning */}
      <div className="fixed bottom-0 left-0 md:left-[144px] xl:left-[304px] right-0 pb-6 md:pb-8 z-50 pointer-events-none">
        <div className="flex justify-start px-4 md:px-0">
          <Button
            type={ButtonType.Primary}
            size="md"
            className={clsx(
              "pointer-events-auto w-auto md:w-auto whitespace-nowrap",
              !isButtonEnabled && "!bg-[#1658FF]/50 !text-white !border-0 cursor-not-allowed opacity-50"
            )}
            onClick={onChoosePreferences}
            disabled={!isButtonEnabled}
          >
            Choose Preferences
          </Button>
        </div>
      </div>
    </>
  );
}

function StockItemRow({ symbol, name, image, isSelected, onSelect, changePercent, latestPrice }: any) {
  const changePercentValue = changePercent ? Number(changePercent) / 100 : 0;
  const latestPriceValue = latestPrice ? Number(latestPrice) : 0;

  return (
    <ListItem 
      onClick={onSelect} 
      className={clsx(
        "cursor-pointer px-4 md:px-6 py-2.5 md:py-3 transition-colors hover:bg-white/5",
        isSelected && "bg-white/[0.03]"
      )}
    >
      <ListItem.Left className="max-w-[60%] md:max-w-none">
        <AssetImage alt={symbol} src={image || ''} width={48} height={48} className="w-10 h-10 md:w-12 md:h-12 flex-none shadow-lg" />
        <div className="ml-2 overflow-hidden">
          <h4 className={clsx("line-clamp-1 text-sm md:text-md transition-colors duration-300", isSelected ? "text-white" : "text-primary")}>{name}</h4>
          <h6 className="text-secondary text-xs md:text-sm">{symbol}</h6>
        </div>
      </ListItem.Left>
      <ListItem.Right className="flex-none gap-2 md:gap-4">
        <div className="text-right">
          <h4 className={clsx(StyleUtils.getChangeStyle(changePercentValue), "text-sm md:text-md")}>
            {changePercentValue !== 0 ? `${MathUtils.round(changePercentValue, 0.01)}%` : '--'}
          </h4>
          <h6 className="text-secondary text-xs md:text-sm">{StringUtils.USD(latestPriceValue)}</h6>
        </div>
        <div className={clsx(
          "h-5 w-5 rounded-full border-2 flex items-center justify-center transition-all duration-300 flex-none",
          isSelected ? "border-success bg-success/20 scale-110" : "border-line-primary group-hover:border-white/30"
        )}>
          {isSelected && <div className="h-2.5 w-2.5 rounded-full bg-success animate-in zoom-in duration-300" />}
        </div>
      </ListItem.Right>
    </ListItem>
  );
}